import {Component, Input, OnInit} from '@angular/core';
import {CompanyImageResponse, CompanyResponse, CountryResponse} from '../../../../../../@shared/models/response.module';
import {CompanyService} from '../../../../../../@shared/services/company.service';
import {_COR_COY_2, _COR_COY_7} from '../../../../../../@shared/models/input.module';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastService} from '../../../../../../@core/services/toast.service';
import {TranslateService} from '@ngx-translate/core';
import {DeleteModal} from '../../../../../../@shared/components/delete-modal/delete-modal';
import {Router} from '@angular/router';
import {StorageService} from '../../../../../../@core/services/storage.service';
import {UserService} from '../../../../../../@core/services/user.service';
import {StaticService} from '../../../../../../@shared/services/static-service';

@Component({
  selector: 'app-edit-company-profile',
  standalone: false,
  templateUrl: './edit-company-profile.html',
  styleUrl: './edit-company-profile.css'
})
export class EditCompanyProfile implements OnInit {
  @Input() company: CompanyResponse

  submitted: boolean = false;
  saving: boolean = false;
  deleting: boolean = false;
  countries: CountryResponse[] = [];

  constructor(private companyService: CompanyService,
              private modal: NgbActiveModal,
              private modalService: NgbModal,
              private toastService: ToastService,
              private translateService: TranslateService,
              private router: Router,
              private storageService: StorageService,
              private userService: UserService,
              private staticService: StaticService) {
  }

  ngOnInit() {
    this.loadCountries();
  }

  loadCountries() {
    this.staticService.getCountries().subscribe({
      next: (countries) => {
        this.countries = countries;
      },
      error: (err) => {
        console.error('Failed to load countries', err);
      }
    });
  }

  // Validation helpers
  get isCompanyNameInvalid(): boolean {
    return this.submitted && !this.company?.company_name?.trim();
  }

  get isFormValid(): boolean {
    return !!this.company?.company_name?.trim();
  }

  save(){
    this.submitted = true;

    if (!this.isFormValid) {
      this.toastService.error(this.translateService.instant('companyProfile.edit.validationError'));
      return;
    }

    this.saving = true;

    let params: _COR_COY_2 = {
      company_name: this.company.company_name.trim(),
      organisation_number: this.company.organisation_number,
      address: this.company.address,
      city: this.company.city,
      postal_code: this.company.postal_code,
      country: '',  // Legacy field - we now use country_id
      country_id: this.company.country_id ? this.company.country_id : null,
      email: this.company.email,
      phone: this.company.phone,
      description: this.company.description,
      // Social Media & Web Links
      facebook_url: this.company.facebook_url ?? undefined,
      instagram_url: this.company.instagram_url ?? undefined,
      twitter_url: this.company.twitter_url ?? undefined,
      youtube_url: this.company.youtube_url ?? undefined,
      tiktok_url: this.company.tiktok_url ?? undefined,
      linkedin_url: this.company.linkedin_url ?? undefined,
      website_url: this.company.website_url ?? undefined,
      merchandise_url: this.company.merchandise_url ?? undefined,
    }
    this.companyService.editCompany(params).subscribe({
      next: (res) => {
        this.company = res;
        this.saving = false;
        this.toastService.success(this.translateService.instant('companyProfile.edit.success'));
        this.modal.close();
      },
      error: (err) => {
        this.saving = false;
        this.toastService.error(err.error?.reason || this.translateService.instant('companyProfile.edit.error'));
      }
    });
  }

  openDeleteConfirmation() {
    const modalRef = this.modalService.open(DeleteModal, {centered: true});
    modalRef.componentInstance.title = this.translateService.instant('companyProfile.delete.title');
    modalRef.componentInstance.description = this.translateService.instant('companyProfile.delete.description', {companyName: this.company.company_name});
    modalRef.componentInstance.confirmLabel = this.translateService.instant('companyProfile.delete.confirm');
    modalRef.componentInstance.cancelLabel = this.translateService.instant('common.cancel');

    modalRef.result.then((result) => {
      if (result) {
        this.deleteCompany();
      }
    }).catch(() => {});
  }

  deleteCompany() {
    this.deleting = true;
    const deletedCompanyId = this.company.company_id;

    this.companyService.deleteCompany().subscribe({
      next: () => {
        this.deleting = false;
        this.storageService.clearSelectedCompany();
        this.toastService.success(this.translateService.instant('companyProfile.delete.success'));

        // Fetch remaining companies and select the first one for the header
        this.userService.getUserEntityRelation().subscribe({
          next: (entities) => {
            const remainingEntities = entities.filter(e => e.entity_id !== deletedCompanyId);
            if (remainingEntities.length > 0) {
              this.storageService.saveSelectedCompany(remainingEntities[0]);
            }
            // Close modal with 'deleted' result - parent will handle navigation
            this.modal.close('deleted');
          },
          error: () => {
            this.modal.close('deleted');
          }
        });
      },
      error: (err) => {
        this.deleting = false;
        this.toastService.error(err.error?.reason || this.translateService.instant('companyProfile.delete.error'));
      }
    });
  }
}
